/**
 * H5 OPS 设备钥（WebCrypto P-256 / ES256）。
 * 私钥以 JWK 存 localStorage，与 deviceCode 绑定；公钥压缩点 hex（66 字符）。
 */

const JWK_KEY = 'DIFI_OPS_P256_JWK';
const PUB_KEY = 'DIFI_OPS_P256_PUB';
const DEVICE_KEY = 'DIFI_OPS_P256_DEVICE';
const REGISTERED_KEY = 'DIFI_OPS_P256_REGISTERED';

const P256_PUB_HEX = /^0[23][0-9a-f]{64}$/;

let memPrivate: CryptoKey | null = null;
let memPublicHex: string | null = null;
let loadDone = false;

const bytesToHex = (bytes: ArrayBuffer | Uint8Array) => {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let out = '';
  for (let i = 0; i < arr.length; i++) out += arr[i]!.toString(16).padStart(2, '0');
  return out;
};

const compressRawPublicKey = (raw: ArrayBuffer): string => {
  const bytes = new Uint8Array(raw);
  // WebCrypto raw SPKI 无 prefix；ECDSA P-256 raw 导出为 65 字节：04||x||y
  if (bytes.length !== 65 || bytes[0] !== 0x04) {
    throw new Error('OPS_KEY_INVALID_PUBLIC');
  }
  const x = bytes.subarray(1, 33);
  const y = bytes.subarray(33, 65);
  const prefix = (y[y.length - 1]! & 1) === 0 ? 0x02 : 0x03;
  const compressed = new Uint8Array(33);
  compressed[0] = prefix;
  compressed.set(x, 1);
  return bytesToHex(compressed);
};

/** 与 http 拦截器对齐的 deviceCode，并写回 localStorage */
export function getOpsDeviceCode(): string {
  let code =
    (localStorage.getItem('DIFI_DEVICE_ID') || localStorage.getItem('deviceCode') || '').trim();
  if (!code) {
    code = `${Date.now()}${Math.floor(Math.random() * 10000)}${Math.random().toString(36).slice(2, 6)}`;
    localStorage.setItem('DIFI_DEVICE_ID', code);
  }
  localStorage.setItem('deviceCode', code);
  if (!localStorage.getItem('DIFI_DEVICE_ID')) {
    localStorage.setItem('DIFI_DEVICE_ID', code);
  }
  return code;
}

const clearStoredKey = () => {
  localStorage.removeItem(JWK_KEY);
  localStorage.removeItem(PUB_KEY);
  localStorage.removeItem(DEVICE_KEY);
  localStorage.removeItem(REGISTERED_KEY);
  memPrivate = null;
  memPublicHex = null;
  loadDone = false;
};

async function importPrivateFromJwk(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign'],
  );
}

async function generateAndPersist(deviceCode: string): Promise<{ publicKey: string }> {
  const pair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['sign', 'verify'],
  );
  const jwk = await crypto.subtle.exportKey('jwk', pair.privateKey);
  const rawPub = await crypto.subtle.exportKey('raw', pair.publicKey);
  const publicKey = compressRawPublicKey(rawPub);
  if (!P256_PUB_HEX.test(publicKey)) throw new Error('OPS_KEY_INVALID_PUBLIC');

  localStorage.setItem(JWK_KEY, JSON.stringify(jwk));
  localStorage.setItem(PUB_KEY, publicKey);
  localStorage.setItem(DEVICE_KEY, deviceCode);
  localStorage.removeItem(REGISTERED_KEY);

  memPrivate = pair.privateKey;
  memPublicHex = publicKey;
  loadDone = true;
  return { publicKey };
}

async function loadOnce(): Promise<void> {
  if (loadDone) return;
  const deviceCode = getOpsDeviceCode();
  const boundDevice = (localStorage.getItem(DEVICE_KEY) || '').trim();
  const jwkRaw = localStorage.getItem(JWK_KEY);
  const pub = (localStorage.getItem(PUB_KEY) || '').trim().toLowerCase();

  if (boundDevice && boundDevice !== deviceCode) {
    clearStoredKey();
  } else if (jwkRaw && P256_PUB_HEX.test(pub)) {
    try {
      const jwk = JSON.parse(jwkRaw) as JsonWebKey;
      memPrivate = await importPrivateFromJwk(jwk);
      memPublicHex = pub;
      loadDone = true;
      return;
    } catch {
      clearStoredKey();
    }
  }

  await generateAndPersist(deviceCode);
}

export async function ensureOpsKey(): Promise<{ publicKey: string; deviceCode: string }> {
  if (!globalThis.crypto?.subtle) {
    throw new Error('OPS_WEBCYPTO_UNAVAILABLE');
  }
  const deviceCode = getOpsDeviceCode();
  await loadOnce();
  if (!memPrivate || !memPublicHex) {
    await generateAndPersist(deviceCode);
  }
  return { publicKey: memPublicHex!, deviceCode };
}

/** UTF-8 → SHA-256 → ECDSA，IEEE P1363 r||s，128 hex */
export async function signOps(message: string): Promise<string> {
  await ensureOpsKey();
  if (!memPrivate) throw new Error('SECURE_KEY_NOT_FOUND');
  const sig = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    memPrivate,
    new TextEncoder().encode(message),
  );
  const hex = bytesToHex(sig);
  if (hex.length !== 128) throw new Error('SECURE_KEY_SIGN_FAILED');
  return hex;
}

export function isOpsPublicKeyRegistered(deviceCode: string): boolean {
  return localStorage.getItem(REGISTERED_KEY) === deviceCode;
}

export function markOpsPublicKeyRegistered(deviceCode: string): void {
  localStorage.setItem(REGISTERED_KEY, deviceCode);
}

export function buildOpsPopSignMessage(
  timestamp: number,
  deviceCode: string,
  devicePublicKey: string,
): string {
  return `${timestamp}${deviceCode}${devicePublicKey}`;
}
